import React, {
  ChangeEventHandler,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";

import debounce from "lodash/debounce";

import { Input, InputProps } from "shared/components/Form/Input";
import { INPUT_SIZES } from "shared/config/constants";

import { SearchIcon, SearchLgIcon } from "./styled";

export type SearchProps = InputProps & {
  id: string;
  onSearch: (query: string) => void;
  debounce?: boolean;
  debounceTimeout?: number;
  disabled?: boolean;
  ActionIcon?: React.ReactNode;
  label?: string;
  value?: string;
};
export const Search: React.FC<SearchProps> = ({
  onSearch,
  id,
  size = INPUT_SIZES.default,
  debounce: shouldDebounce,
  debounceTimeout = 500,
  disabled,
  ActionIcon,
  value,
  ...props
}) => {
  // Use local state for immediate UI updates
  const [localValue, setLocalValue] = useState(value || "");

  // Create stable debounced function
  const debouncedSearchRef = useRef(null);

  useEffect(() => {
    if (shouldDebounce) {
      debouncedSearchRef.current = debounce((searchValue) => {
        onSearch(searchValue);
      }, debounceTimeout);
    }

    return () => {
      if (
        debouncedSearchRef.current &&
        typeof debouncedSearchRef.current.cancel === "function"
      ) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, [shouldDebounce, debounceTimeout, onSearch]);

  // Only update from external value when it's explicitly cleared
  useEffect(() => {
    if (value === "") {
      setLocalValue("");
      if (
        debouncedSearchRef.current &&
        typeof debouncedSearchRef.current.cancel === "function"
      ) {
        debouncedSearchRef.current.cancel();
      }
    }
  }, [value]);

  const handleSearch = useCallback(
    (searchValue) => {
      onSearch(searchValue);
    },
    [onSearch]
  );

  const Icon = size === INPUT_SIZES.lg ? SearchLgIcon : SearchIcon;

  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);

      if (shouldDebounce && debouncedSearchRef.current) {
        debouncedSearchRef.current(newValue);
      } else {
        handleSearch(newValue);
      }
    },
    [shouldDebounce, handleSearch]
  );

  return (
    <Input
      data-testid="search"
      id={id}
      name="search-input"
      value={localValue}
      suffix={
        ActionIcon || (
          <Icon
            onClick={() => {
              handleSearch(localValue);
              if (
                debouncedSearchRef.current &&
                typeof debouncedSearchRef.current.cancel === "function"
              ) {
                debouncedSearchRef.current.cancel();
              }
            }}
          />
        )
      }
      onChange={onChange}
      onPressEnter={(event: any) => {
        handleSearch(event.target.value);
        if (
          debouncedSearchRef.current &&
          typeof debouncedSearchRef.current.cancel === "function"
        ) {
          debouncedSearchRef.current.cancel();
        }
      }}
      allowClear
      size={size}
      withContainer
      disabled={disabled}
      {...props}
    />
  );
};
